/**
 * "Download on the App Store" / "Get it on Google Play" badge links.
 *
 * Trade Loop stopped being installable from the browser when the PWA was retired (Card 30):
 * the manifest that made Chrome offer "Install app" and the iOS "Add to Home Screen" banners
 * are gone, and nothing replaced them. The two native apps are the install channel now, so the
 * web app has to point at them the way every other product does — with the stores' own badges.
 *
 * ## Why this is a component and not three copies of two `<a>` tags
 *
 * The badges are the one surface in this app that is governed by somebody else's rules, and both
 * rulebooks are about geometry. Apple's marketing guidelines set a minimum clear space of 10% of
 * the badge's height on every side and forbid altering the artwork; Google's set clear space at a
 * quarter of the badge height and likewise forbid recolouring or restyling. Getting that right
 * once, here, is the point — a placement only decides where the row goes, never how a badge is
 * drawn.
 *
 * ## Not rendered inside the native apps
 *
 * This same React bundle IS the iOS and Android app (Capacitor loads `dist/`). A store badge
 * inside the app is at best a link to the page the user already installed from, and at worst an
 * App Review finding — 3.1.1 territory, and the same instinct behind the `isNative` guard on
 * `CheckoutButton`. `isNative` is a module-level constant decided at import time, so this is a
 * hard branch, not a runtime toggle.
 */

import { isNative } from '@/utils/platform';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  STORE_LISTING_NAME,
} from '@/config/app-store-links';
import appStoreBadge from '@/assets/app-store-badge.svg';
import googlePlayBadge from '@/assets/google-play-badge.png';

/**
 * Badge artwork height in px — the shared measure both badges are sized from, so they read as
 * one pair rather than two pasted images.
 *
 * The two stores ship their clear space differently, which is the entire reason these numbers
 * exist rather than a single `h-10` on both images:
 *
 * - **Apple** ships the badge alone (`viewBox="0 0 119.66 40"`). The mandated ≥10% clear space
 *   has to be added by us, so it is padding on the link.
 * - **Google** bakes it in: the file is 646×250 with a 564×168 badge centred inside 41px of
 *   transparent margin (41/168 ≈ the quarter-height Google requires). Sizing that image to
 *   `BADGE_HEIGHT` would make Google's badge a quarter smaller than Apple's, so the IMAGE is
 *   scaled by 250/168 and the badge inside it lands at `BADGE_HEIGHT`.
 *
 * Net effect: both badges are drawn at the same visual height, and both stores' clear-space
 * rules hold, without either asset being edited.
 */
const BADGE_HEIGHT = 40;
const APPLE_CLEAR_SPACE = BADGE_HEIGHT * 0.1;
const PLAY_IMAGE_HEIGHT = BADGE_HEIGHT * (250 / 168);

/**
 * Shared link styling. `focus-visible:ring-*` rather than `focus:ring-*` matches the marketing
 * footer's links: the ring is for keyboard users and should not fire on a mouse click. The
 * offset colour is deliberately unset so the ring sits on whatever background the placement has
 * — these render on white, on a light gradient and on `bg-gray-950`.
 */
const LINK_CLASSES =
  'inline-flex rounded-lg transition-opacity hover:opacity-80 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

/**
 * Whether there is anything to show at all.
 *
 * Exported because every placement wraps the row in a heading ("Get the mobile app") and its own
 * chrome — a border, a card, a divider. `AppStoreBadges` returning `null` would leave all of that
 * behind as a labelled empty space, so the placement has to make the same decision the component
 * does. Both inputs are fixed at import time, so this is a constant and not a hook.
 */
export const APP_STORE_BADGES_VISIBLE = !isNative && Boolean(APP_STORE_URL || PLAY_STORE_URL);

interface AppStoreBadgesProps {
  /** Layout hook for the row itself (alignment, spacing above/below). */
  className?: string;
}

/**
 * The badge row. Renders nothing at all inside the native apps, and nothing if neither store
 * URL is configured — a placement can therefore drop this in unconditionally.
 */
export default function AppStoreBadges({ className = '' }: AppStoreBadgesProps) {
  if (!APP_STORE_BADGES_VISIBLE) return null;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
      {APP_STORE_URL && (
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={LINK_CLASSES}
          style={{ padding: APPLE_CLEAR_SPACE }}
        >
          {/* The alt text IS the link's accessible name, so it says what activating the link
              does and names the listing as it is titled on the store. */}
          <img
            src={appStoreBadge}
            alt={`Download ${STORE_LISTING_NAME} on the App Store`}
            style={{ height: BADGE_HEIGHT }}
            className="w-auto"
          />
        </a>
      )}

      {PLAY_STORE_URL && (
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={LINK_CLASSES}
        >
          <img
            src={googlePlayBadge}
            alt={`Get ${STORE_LISTING_NAME} on Google Play`}
            style={{ height: PLAY_IMAGE_HEIGHT }}
            className="w-auto"
          />
        </a>
      )}
    </div>
  );
}
